import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpResponse,
  HttpXsrfTokenExtractor
} from '@angular/common/http';
import { environment } from '../../../../environments/environment';

/**
 * Servicio de Consumo de Autorización (BFF - Backend For Frontend).
 * * Este servicio actúa como el puente de comunicación entre la aplicación Angular y el 
 * microservicio de autorización. A diferencia de un servicio de autenticación, este
 * se centra en la recuperación de capacidades (permisos) y la gestión de la seguridad 
 * a nivel de sesión del navegador.
 * * ### Responsabilidades Críticas:
 * 1. **Handshake CSRF:** Inicialización del mecanismo de protección contra Cross-Site Request Forgery.
 * 2. **Context Discovery:** Recuperación del grafo completo de permisos y grupos del usuario.
 * 3. **Session Integrity:** Gestión de cabeceras de seguridad mediante el uso de `withCredentials`.
 * * @author César Erick Bustamante Alva
 * @see {@link AuthService}
 * @see {@link HttpClient}
 */
@Injectable({
  providedIn: 'root'
})
export class CsrfService {

  /** * Punto de entrada base para el microservicio de autorización. 
   * Recuperado de las variables de entorno para facilitar el despliegue multi-entorno.
   */
  private apiUrl = environment.BFF_BACKEND_URL;

  /**
   * Inicializa el cliente HTTP.
   * @param http Cliente para la ejecución de peticiones REST.
   */
  constructor(
    private http: HttpClient,
    private tokenExtractor: HttpXsrfTokenExtractor // ← inyecta en el constructor
  ) { }

  /**
   * Ejecuta el flujo de inicialización del Token CSRF (Cross-Site Request Forgery).
   * * * **Mecánica:**
   * 1. Solicita al endpoint `/csrf` la generación de un token de seguridad.
   * 2. El servidor responde con una cookie `XSRF-TOKEN` (HttpOnly=false para lectura del cliente).
   * 3. El {@link HttpXsrfTokenExtractor} de Angular captura el valor.
   * 4. Peticiones mutables posteriores (POST, PUT, DELETE) incluirán automáticamente el header `X-XSRF-TOKEN`.
   * * *Nota: Requiere que el backend tenga configurada la política de SameSite y CORS de forma estricta.*
   */
  csrf(): Promise<void> {
    return new Promise((resolve) => {
      this.http.get<any>(
        `${this.apiUrl}${environment.BFF_BACKEND_SERVICE_CSRF}`,
        {
          observe: 'response',
          withCredentials: true
        }
      ).subscribe({
        next: (response: HttpResponse<any>) => {
          console.log('CSRF Handshake exitoso.');

          // ✅ tokenExtractor ya está inyectado a nivel de clase
          const csrfCookie = this.tokenExtractor.getToken();

          if (!csrfCookie) {
            console.warn('Advertencia: No se detectó la cookie XSRF-TOKEN.');
          }

          resolve(); // ← avisa que terminó para que AuthService pueda continuar
        },
        error: (error) => {
          console.error('Fallo crítico en la inicialización de seguridad CSRF:', error);
          resolve(); // ← resuelve igual para no bloquear el flujo
        }
      });
    });
  }

  /** Limpia la cookie CSRF al cerrar sesión */
  clearCsrfCookie(): void {
    const cookieName = environment.CSRF.COOKIE_NAME;
    // Para eliminar una cookie hay que setearla con maxAge=0 y la misma configuración
    document.cookie = `${cookieName}=; Max-Age=0; path=/; domain=${environment.CSRF.COOKIE_DOMAIN}; secure; samesite=strict`;
  }
}